import { apiClient } from '../utils/apiClient';

interface Enquiry {
  name: string;
  email: string;
  phone: string;
  company?: string;
  enquiryType: 'tour' | 'membership' | 'enterprise' | 'general';
  message: string;
}

export interface BookingItem {
  resource_type: 'DESK' | 'MEETING_ROOM';
  resource_id: string[] | string;
  booking_type: 'daily' | 'monthly' | 'hourly';
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
}

export interface CreateBookingRequest {
  mobile_number: string;
  name: string;
  email: string;
  address: string;
  gstNumber: string;
  items: BookingItem[];
}

// Legacy interface for backward compatibility
export interface LegacyCreateBookingRequest {
  desk_codes: string[];
  booking_type: string;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  mobile_number: string;
  name: string;
  email: string;
  gstNumber: string;
}

export interface BookingItemResponse {
  id: string;
  resource_type: 'DESK' | 'MEETING_ROOM';
  resource_id: string;
  resource_name: string;
  booking_type: 'daily' | 'monthly' | 'hourly';
  start_datetime: string;
  end_datetime: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  item_status: string;
  cancelled_at: string | null;
}

export interface CreateBookingResponse {
  id: string;
  booking_code: string;
  user_name: string;
  mobile_number: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  currency: string;
  order_status: string;
  razorpay_order_id: string;
  created_at: string;
  items: BookingItemResponse[];
}

export interface VerifyPaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface VerifyPaymentResponse {
  id?: string;
  booking_code: string;
  user_name?: string;
  mobile_number?: string;
  base_amount?: string;
  cgst_amount?: string;
  sgst_amount?: string;
  total_amount?: string;
  currency?: string;
  order_status: string;
  payment_status?: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  paid_at?: string;
  created_at?: string;
  items?: BookingItemResponse[];
}

// API Response from /api/booking-orders/user/{mobile_number}
export interface BookingOrdersResponse {
  id: string;
  booking_code: string;
  user_name: string;
  mobile_number: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  currency: string;
  order_status: string;
  razorpay_order_id: string | null;
  items: BookingItemResponse[];
  created_at: string;
}

export interface GetBookingsApiResponse {
  orders: BookingOrdersResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Mapped response for UI
export interface UserBooking {
  id: string;
  booking_code: string;
  user_name: string;
  mobile_number: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  currency: string;
  status: string;
  created_at: string;
  razorpay_order_id: string | null;
  items: BookingItemResponse[];
}

export interface UserBookingsResponse {
  current_bookings: UserBooking[];
  previous_bookings: UserBooking[];
}

// Legacy interface for backward compatibility
export interface LegacyCreateBookingResponse {
  booking_id: string;
  user_name: string;
  desk_codes: string[];
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  currency: string;
  status: string;
  created_at: string;
  razorpay_order_id: string;
}

export const BookingService = {
  async createBooking(bookingData: CreateBookingRequest | LegacyCreateBookingRequest): Promise<CreateBookingResponse> {
    try {
      // Check if it's the new format or legacy format
      if ('items' in bookingData) {
        // New API format
        const response = await apiClient.post('/api/booking-orders', bookingData);
        return response?.data || {};
      } else {
        // Legacy format - convert to new format
        const legacyData = bookingData as LegacyCreateBookingRequest;
        const newBookingData: CreateBookingRequest = {
          mobile_number: legacyData.mobile_number,
          name: legacyData.name,
          email: legacyData.email,
          address: '',
          gstNumber: legacyData.gstNumber,
          items: [
            {
              resource_type: 'DESK',
              resource_id: legacyData.desk_codes,
              booking_type: legacyData.booking_type as 'daily' | 'monthly',
              start_date: legacyData.start_date,
              start_time: legacyData.start_time,
              end_date: legacyData.end_date,
              end_time: legacyData.end_time
            }
          ]
        };
        
        const response = await apiClient.post('/api/booking-orders', newBookingData);
        return response?.data || {};
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create booking');
    }
  },

  async bookTour(): Promise<void> {
    console.log('Book tour functionality not implemented yet');
  },

  async startMembership(): Promise<void> {
    console.log('Start membership functionality not implemented yet');
  },

  async contactSales(): Promise<void> {
    console.log('Contact sales functionality not implemented yet');
  },

  async submitEnquiry(enquiry: Enquiry): Promise<void> {
    console.log('Submit enquiry functionality not implemented yet', enquiry);
  },

  async verifyPayment(paymentData: VerifyPaymentRequest): Promise<VerifyPaymentResponse> {
    try {
      const response = await apiClient.post('/api/booking-orders/verify-order-payment', paymentData);
      return response?.data || {};
    } catch (error: any) {
      throw new Error(error.message || 'Failed to verify payment');
    }
  },

  async getUserBookings(mobileNumber: string): Promise<UserBookingsResponse> {
    try {
      console.log('📱 BookingService.getUserBookings called with:', mobileNumber);
      
      const response = await apiClient.get(`/api/booking-orders/user/${mobileNumber}`);
      
      console.log('📦 API Response received:', response);
      
      const apiResponse: GetBookingsApiResponse = response?.data || { orders: [], total: 0, page: 1, page_size: 10, total_pages: 0 };
      
      console.log('🔍 API Response structure:', {
        has_orders: !!apiResponse.orders,
        orders_length: apiResponse.orders?.length || 0,
        total: apiResponse.total,
        page: apiResponse.page,
        page_size: apiResponse.page_size,
        total_pages: apiResponse.total_pages
      });
      
      // Map API response to UI response
      const allBookings: UserBooking[] = (apiResponse.orders || []).map(order => {
        console.log('🔄 Mapping order:', order.booking_code, 'status:', order.order_status);
        return {
          id: order.id,
          booking_code: order.booking_code,
          user_name: order.user_name,
          mobile_number: order.mobile_number,
          base_amount: order.base_amount,
          cgst_amount: order.cgst_amount,
          sgst_amount: order.sgst_amount,
          total_amount: order.total_amount,
          currency: order.currency,
          status: order.order_status,
          created_at: order.created_at,
          razorpay_order_id: order.razorpay_order_id,
          items: order.items
        };
      });

      console.log('📊 All bookings mapped:', {
        total_mapped: allBookings.length,
        bookings: allBookings.map(b => ({ code: b.booking_code, status: b.status }))
      });

      // Separate current (confirmed) and previous (cancelled) bookings
      const current_bookings = allBookings.filter(b => b.status === 'confirmed');
      const previous_bookings = allBookings.filter(b => b.status !== 'confirmed');

      console.log('✅ Bookings separated:', {
        current: current_bookings.length,
        previous: previous_bookings.length
      });

      return {
        current_bookings,
        previous_bookings
      };
    } catch (error: any) {
      console.error('❌ BookingService.getUserBookings error:', {
        error: error.message,
        status: error.status,
        response: error.response,
        full_error: error
      });
      throw new Error(error.message || 'Failed to fetch user bookings');
    }
  }
};
